import { zoomTo } from '#~/control/movement/commands/index.js';
import { useRatePref, useTempoPref } from '#~/control/movement/hooks.js';
import { computeZoom, Facet, Tempo } from '#~/control/movement/index.js';
import { useZoomWheel } from '#~/control/wheel/hooks.js';
import { isPrimaryPointer } from '../utils.js';
import type { PointerHandle } from './pointer/pointer.js';
import type { Wiring } from './types.js';
import type { CanvasContext } from '@studio/design-system/canvas';
import type { Coordinate } from '@studio/js-utils/geometry';
import type { Device } from '@studio/models/device';
import { useLatestFn, χref } from '@studio/react-utils/hooks';
import { useEffect, useRef, useState } from 'react';
import type { PointerEvent, RefObject } from 'react';

/** @internal */
export const useWiring = (
  deviceId: Device['id'],
  contextRef: RefObject<CanvasContext | null>,
  pointerRef: RefObject<PointerHandle | null>
): Wiring => {
  const [pointer, setPointer] = useState<Coordinate>();
  const isPanning = useRef(false);

  const [zoomRate] = useRatePref(deviceId, Facet.Zoom);
  const [ptRate] = useRatePref(deviceId, Facet.PanTilt);
  const [zoomTempo] = useTempoPref(deviceId, Facet.Zoom);
  const [ptTempo] = useTempoPref(deviceId, Facet.Zoom);

  const [wheelSpeed, onWheel] = useZoomWheel();

  // Handle canvas drawing
  const handleDrawCanvas = useLatestFn(() => {
    const pointerComponent = χref(pointerRef);
    const canvas = χref(contextRef);

    if (!pointerComponent) {
      return;
    }
    const zooming = computeZoom({ tempo: zoomTempo, rate: zoomRate, wheelSpeed });

    if (zooming > 0) {
      pointerComponent.changePointer('zoomIn');
    } else if (zooming < 0) {
      pointerComponent.changePointer('zoomOut');
    } else if (isPanning.current) {
      pointerComponent.changePointer('click');
    } else {
      pointerComponent.changePointer('default');
    }

    if (pointer) {
      pointerComponent.showPointer();
      pointerComponent.updatePosition(pointer.x, pointer.y);
    } else {
      pointerComponent.hidePointer();
    }
    canvas?.draw();
  });

  // Handle pointer events.
  const handlePointerEvent = useLatestFn((event: PointerEvent) => {
    if (!isPrimaryPointer(event)) {
      return;
    }

    if (event.type === 'pointerleave') {
      setPointer(undefined);

      return;
    }

    // Update the current pointer position.
    const rect = χref(contextRef)?.canvas?.getBoundingClientRect();

    setPointer(rect ? { x: event.clientX - rect.left, y: event.clientY - rect.top } : undefined);
  });

  // Handle zooming.
  const handleAction = useLatestFn((operation: 'zoom' | 'pan', stop?: boolean) => {
    const rect = contextRef.current?.canvas?.getBoundingClientRect();

    if (!rect || !pointer) {
      return;
    }

    const { width, height } = rect;

    const pos = {
      x: Math.round((pointer.x / width) * 100) / 100,
      y: Math.round((pointer.y / height) * 100) / 100
    };

    const zoomSpeed =
      zoomTempo === Tempo.Fixed ? zoomRate.value * Math.sign(wheelSpeed) : wheelSpeed;

    const getPreferenceValue = (value: number) => {
      if (ptTempo === Tempo.Fixed) {
        return ptRate.value * Math.sign(value);
      }
      return ptRate.value * value;
    };
    const pan = (pos.x - 0.5) * 2;
    const tilt = -(pos.y - 0.5) * 2;

    zoomTo(deviceId, {
      pos,
      zoom: stop ? 0 : zoomSpeed,
      panTilt: {
        pan: stop ? 0 : getPreferenceValue(pan),
        tilt: stop ? 0 : getPreferenceValue(tilt)
      },
      operation
    });
  });

  // Handle Click or Hotkey scenario.
  useEffect(() => {
    const canvas = contextRef.current?.canvas;
    if (!canvas) {
      return;
    }
    let interval: NodeJS.Timeout | undefined = undefined;
    const startPanning = () => () => {
      clearInterval(interval);
      isPanning.current = true;

      interval = setInterval(() => handleAction('pan'), 200);
      handleAction('pan');
    };
    const stopPanning = () => () => {
      handleAction('pan', true);
      isPanning.current = false;

      if (!interval) {
        return;
      }
      clearInterval(interval);
      interval = undefined;
    };

    const pointerdown = startPanning();
    const pointerup = stopPanning();
    const mouseout = stopPanning();
    // const handleKeydown = () => handleAction('pan');

    canvas.addEventListener('pointerdown', pointerdown);
    canvas.addEventListener('pointerup', pointerup);
    canvas.addEventListener('mouseout', mouseout);
    // window.addEventListener('keydown', handleKeydown);

    return () => {
      canvas.removeEventListener('pointerdown', pointerdown);
      canvas.removeEventListener('pointerup', pointerup);
      canvas.removeEventListener('mouseout', mouseout);
      // window.removeEventListener('keydown', handleKeydown);
    };
  }, [contextRef.current?.canvas]);

  return {
    deviceId,
    handleDrawCanvas,
    handlePointerEvent,
    handleWheelEvent: (ev) => {
      onWheel(ev);
      handleAction('zoom');
    }
  };
};
